/**
 * Obligation planning activity service — shared layer (bez feature importa).
 * CRUD i pretraga aktivnosti planiranja obaveze.
 */
import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { getAuthUser, sanitizeActivitySavePayload } from '@/shared/auth/obligationAuth';

const OBLIGATION = API_ENDPOINTS.OGP.OBLIGATION;

/**
 * Pretraga aktivnosti obaveze po `obligation_id`.
 * @param {object} [filters] - Filteri pretrage.
 * @param {string|number} filters.obligation_id - ID obaveze (obavezno).
 * @returns {Promise<object>} API odgovor sa listom aktivnosti.
 */
export async function searchObligationActivities(filters = {}) {
  const obligationId = filters.obligation_id;
  if (!obligationId || String(obligationId).trim() === '') {
    throw new Error('Za pretragu aktivnosti potreban je obligation_id');
  }
  const response = await ApiService.post(OBLIGATION.ACTIVITY_SEARCH, {
    obligation_id: String(obligationId).trim(),
  });
  return response.data;
}

/**
 * Snima ili ažurira aktivnost obaveze; payload prolazi kroz `sanitizeActivitySavePayload`.
 * @param {object} activity - Podaci aktivnosti.
 * @param {object} [options] - Kontekst snimanja.
 * @param {string} [options.saveContext] - Kontekst za ACL sanitizaciju.
 * @returns {Promise<object>} Sačuvani zapis sa BE-a.
 */
export async function saveOrUpdateObligationActivity(activity, options = {}) {
  const user = await getAuthUser();
  const body = sanitizeActivitySavePayload(activity, {
    user,
    saveContext: options.saveContext,
  });
  const response = await ApiService.post(OBLIGATION.ACTIVITY_SAVE_OR_UPDATE, body);
  return response.data;
}

/**
 * Snima ručni redosled aktivnosti obaveze.
 * @param {string|number} obligationId - ID obaveze.
 * @param {Array<string|number>} activityIds - ID-jevi aktivnosti u novom redosledu.
 * @returns {Promise<object>} API odgovor.
 */
export async function reorderObligationActivities(obligationId, activityIds) {
  const response = await ApiService.post(OBLIGATION.ACTIVITY_REORDER, {
    obligation_id: obligationId,
    activity_ids: activityIds,
  });
  return response.data;
}

/**
 * Arhivira aktivnost obaveze po ID-u.
 * @param {string|number} activityId - ID aktivnosti.
 * @returns {Promise<object>} API odgovor.
 */
export async function archiveObligationActivity(activityId) {
  const response = await ApiService.post(OBLIGATION.ACTIVITY_ARCHIVE_SEARCH, {
    activity_id: activityId,
  });
  return response.data;
}

/**
 * Vraća poslednji (najnoviji) implementation zapis iz niza.
 * @param {Array|null|undefined} implementations - Lista implementation zapisa.
 * @returns {object|null} Poslednji element ili `null`.
 */
export function pickLatestObligationImplementation(implementations) {
  if (!Array.isArray(implementations) || implementations.length === 0) return null;
  return implementations[implementations.length - 1];
}

/**
 * Pretraga implementation zapisa za obavezu u akcionom planu.
 * @param {object} params
 * @param {string|number} params.action_plan_id - ID akcionog plana.
 * @param {string|number} params.obligation_id - ID obaveze.
 * @returns {Promise<object>} API odgovor sa implementation podacima.
 */
export async function searchObligationImplementations({ action_plan_id, obligation_id }) {
  if (!action_plan_id || !obligation_id) {
    throw new Error('Za pretragu implementation potrebni su action_plan_id i obligation_id');
  }
  const response = await ApiService.post(OBLIGATION.IMPLEMENTATION_SEARCH, {
    action_plan_id,
    obligation_id,
  });
  return response.data;
}

/**
 * Snima ili ažurira implementation zapis obaveze.
 * @param {object} payload - Implementation payload za BE.
 * @returns {Promise<object>} Sačuvani zapis sa BE-a.
 */
export async function saveOrUpdateObligationImplementation(payload) {
  const response = await ApiService.post(OBLIGATION.IMPLEMENTATION_SAVE_OR_UPDATE, payload);
  return response.data;
}
